import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";

import createWebWorker, { WorkerInitializer } from "src/workers/WebWorker";
import worker from "src/workers/app.worker";

import type {
  Nullable,
  FilterWorkerContextType,
  WorkerResponse,
} from "src/ts-types";

export const FilterWorkerContext =
  createContext<Nullable<FilterWorkerContextType>>(null);

interface FilterWorkerProviderProps {
  children: ReactNode;
}

export const FilterWorkerProvider = ({
  children,
}: FilterWorkerProviderProps) => {
  const [filterWorker] = useState<Worker>(() =>
    createWebWorker(worker as WorkerInitializer),
  );

  const latestRequestIdRef = useRef<number>(0);
  const callbackRef = useRef<Nullable<(result: unknown) => void>>(null);

  useEffect(() => {
    filterWorker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const { requestId, result } = event.data;

      // Only process if this response matches the latest request (latest-wins)
      if (requestId === latestRequestIdRef.current && callbackRef.current) {
        callbackRef.current(result);
      }
    };

    return () => {
      filterWorker.terminate();
    };
  }, [filterWorker]);

  const postMessage = useCallback(
    (data: unknown, onMessage: (result: unknown) => void) => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      callbackRef.current = onMessage;

      filterWorker.postMessage({ requestId, payload: data });
    },
    [filterWorker],
  );

  return (
    <FilterWorkerContext.Provider value={{ postMessage }}>
      {children}
    </FilterWorkerContext.Provider>
  );
};
